import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Stack, Typography } from "@mui/material";
import { useConfirmThanksToYouWithdrawalMutation } from "Api/Mutations/Lead/useConfirmThanksToYouWithdrawalMutation";
import { AuthPageLayout } from "Components/Auth/Shared/AuthPageLayout";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { ThanksToYouWithdrawalStatus } from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import Error404Page from "Pages/Error/Error404Page";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { useParams } from "react-router-dom";

const RESOURCES = Resources.Components.Affiliate.ThanksToYou.WithdrawalPage;

const ICON_SIZE = 64;

/**
 * What the page tells the person. Settled = leaving is done (now or earlier) and the app is the
 * next stop; otherwise the link did not work and the Díky Tobě section is where a new one comes from.
 */
type Outcome = {
  resources: typeof RESOURCES.Success;
  isSettled: boolean;
};

/** An unknown token is a URL that does not exist — that is the app's 404, not an outcome. */
type KnownStatus = Exclude<
  ThanksToYouWithdrawalStatus,
  ThanksToYouWithdrawalStatus.NotFound
>;

const OUTCOME_BY_STATUS: Record<KnownStatus, Outcome> = {
  [ThanksToYouWithdrawalStatus.Success]: {
    resources: RESOURCES.Success,
    isSettled: true,
  },
  [ThanksToYouWithdrawalStatus.AlreadyWithdrawn]: {
    resources: RESOURCES.AlreadyWithdrawn,
    isSettled: true,
  },
  [ThanksToYouWithdrawalStatus.Expired]: {
    resources: RESOURCES.Expired,
    isSettled: false,
  },
};

const ERROR_OUTCOME: Outcome = { resources: RESOURCES.Error, isSettled: false };

/** Undefined until the person confirmed and the server answered. */
const getOutcome = (
  isError: boolean,
  status: KnownStatus | undefined,
): Outcome | undefined => {
  if (isError) {
    return ERROR_OUTCOME;
  }

  return status === undefined ? undefined : OUTCOME_BY_STATUS[status];
};

/**
 * The link from the e-mail that completes leaving Díky Tobě (ZS-1623). Works signed in or not — the
 * token is the proof. Nothing happens on load: mail gateways and link scanners open links in headless
 * browsers, and leaving is for good, so the person confirms with a click first. Then the page explains
 * the outcome: done, already done (a second click) or a stale link (expired or replaced by a newer
 * e-mail). A link nobody issued is a 404.
 */
export const ThanksToYouWithdrawalPage = () => {
  const { t } = useResource();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const {
    mutate: confirm,
    data,
    isError,
    isPending,
  } = useConfirmThanksToYouWithdrawalMutation();

  const status = data?.status;

  if (!token || status === ThanksToYouWithdrawalStatus.NotFound) {
    return <Error404Page />;
  }

  const outcome = getOutcome(isError, status);

  if (outcome === undefined) {
    return (
      <AuthPageLayout
        title={t(RESOURCES.Title)}
        pageType="other"
        isFooterHidden
      >
        <Stack gap={4} mt={4} mx={4} textAlign="center">
          <Typography fontSize="1rem">{t(RESOURCES.Prompt)}</Typography>
          <PrimaryButton
            fullWidth
            color="error"
            onClick={() => confirm(token)}
            isLoading={isPending}
          >
            {t(RESOURCES.ConfirmButton)}
          </PrimaryButton>
        </Stack>
      </AuthPageLayout>
    );
  }

  const { isSettled, resources } = outcome;
  const handleContinue = () =>
    navigate(appUrl(isSettled ? "dashboard" : "affiliate"));

  return (
    <AuthPageLayout
      title={t(resources.Title)}
      pageType="other"
      isFooterHidden
      icon={
        isSettled ? (
          <CheckCircleOutlineIcon
            color="success"
            sx={{ fontSize: ICON_SIZE }}
          />
        ) : (
          <WarningAmberOutlinedIcon
            color="warning"
            sx={{ fontSize: ICON_SIZE }}
          />
        )
      }
    >
      <Stack gap={4} mt={4} mx={4} textAlign="center">
        <Typography fontSize="1rem">{t(resources.Text)}</Typography>
        <PrimaryButton fullWidth color="primary" onClick={handleContinue}>
          {t(
            isSettled ? RESOURCES.ContinueButton : RESOURCES.OpenSectionButton,
          )}
        </PrimaryButton>
      </Stack>
    </AuthPageLayout>
  );
};

export default ThanksToYouWithdrawalPage;
